import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useAppStore } from '@/hooks/useAppStore'
import { Topbar } from '@/components/shared/Topbar'
import { Atmosphere } from '@/components/shared/Atmosphere'
import { DNA_PROFILES } from '@/data/destinations'

export function EmailGateScreen() {
  const { selectedDestination, dnaKey, leadEmail, setLeadEmail, setScreen } = useAppStore()
  const [email, setEmail] = useState(leadEmail || '')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const profile = DNA_PROFILES[dnaKey]

  const proceed = () => setScreen('card')

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault()
    const trimmed = email.trim()
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(trimmed)) {
      setError('Please enter a valid email address.')
      return
    }
    setError('')
    setSubmitting(true)
    setLeadEmail(trimmed)
    try {
      await fetch('/api/lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: trimmed,
          brand: 'passage',
          dna: profile?.name || '',
          destination: selectedDestination?.name || '',
        }),
      })
    } catch {
      // Network/API failure — never block the demo, proceed regardless.
    } finally {
      setSubmitting(false)
      proceed()
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'radial-gradient(ellipse at 50% 30%, #3A2312 0%, #241209 55%, #180C05 100%)',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <Atmosphere count={18} />
      <div style={{ position: 'relative', zIndex: 5 }}>
        <Topbar backTo="journey" />
      </div>

      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', position: 'relative', zIndex: 3 }}>
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
          className="p-glow-card"
          style={{
            width: '100%', maxWidth: 460,
            border: '1px solid rgba(212,160,23,0.3)',
            background: 'rgba(20,10,4,0.72)',
            padding: '44px 36px',
            textAlign: 'center',
          }}
        >
          <div style={{
            width: 56, height: 56, borderRadius: '50%', margin: '0 auto 22px',
            border: '1px solid rgba(212,160,23,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 24, color: 'var(--gold2)', background: 'rgba(212,160,23,0.08)',
          }}>
            ✉
          </div>

          <h1 style={{ fontFamily: 'var(--serif-display)', fontSize: 28, color: '#F5EDD8', marginBottom: 12 }}>
            Unlock <em style={{ color: 'var(--gold2)', fontStyle: 'italic' }}>Your Journey</em>
          </h1>

          <p style={{ fontFamily: 'var(--serif-body)', fontSize: 14, color: 'rgba(245,237,216,0.68)', lineHeight: 1.75, marginBottom: 26, maxWidth: 380, marginLeft: 'auto', marginRight: 'auto' }}>
            Enter your email and we'll craft a personalised narrative itinerary — every deal, saving and curator's note woven in. Plus a beautiful journey card to take with you.
          </p>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                style={{
                  flex: '1 1 220px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(212,160,23,0.3)',
                  padding: '12px 16px', fontFamily: 'var(--serif-body)', fontSize: 14, color: 'var(--parch)', outline: 'none',
                }}
              />
              <motion.button
                type="submit"
                disabled={submitting}
                whileTap={{ scale: 0.97 }}
                className="p-glow-gold"
                style={{
                  flex: '0 0 auto', padding: '12px 22px', background: 'linear-gradient(90deg, var(--gold), var(--gold2))',
                  color: '#241209', border: 'none', fontFamily: 'var(--serif-caps)', fontSize: 11, letterSpacing: '0.1em',
                  textTransform: 'uppercase', cursor: submitting ? 'wait' : 'pointer', opacity: submitting ? 0.75 : 1,
                }}
              >
                {submitting ? 'Building…' : 'Build it →'}
              </motion.button>
            </div>
            {error && (
              <div style={{ fontFamily: 'var(--serif-body)', fontSize: 12, color: '#E8A87C', fontStyle: 'italic' }}>{error}</div>
            )}
          </form>

          <p style={{ fontFamily: 'var(--serif-caps)', fontSize: 9, letterSpacing: '0.08em', color: 'rgba(245,237,216,0.4)', marginTop: 20, textTransform: 'uppercase' }}>
            Private · No spam · Just your journey and exclusive partner offers
          </p>
        </motion.div>
      </div>
    </div>
  )
}
